import { FGCheckboxInput, FieldGroup, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useLocation } from "react-router";

import { RequestApi, VerificationsValueDto } from "../api";
import { useEventsContext } from "../contexts";
import { useApiService, useCrudApi } from "../hooks";
import { ETLCodes } from "../locales";
import { SmallFormGenerator } from "./";

export interface IVerificationTabProps {
  requestId: number;
  editMode: boolean;
  minLabelWidth?: number;
  statusETLCode: ETLCodes;
}

export const VerificationTab: React.FunctionComponent<IVerificationTabProps> = ({
  requestId,
  editMode,
  minLabelWidth = 300,
  statusETLCode,
}) => {
  const requestApi = useApiService(RequestApi);
  const [currentRequestStatus, currentRequestStatusIdLoading] = useApiEffect(
    () => requestApi.getStatusCurrentRequest(requestId),
    [requestId]
  );
  const { data, loading, saving, saveItem, refresh } = useCrudApi(
    {
      getApiFn: (id = requestId) => requestApi.getVerifications(id),
      saveApiFn: (d: VerificationsValueDto[]) => requestApi.saveVerifications(+requestId, d),
      deleteApiFn: (d) => {},
      onDeletedRoute: () => "",
    },
    [requestId]
  );

  const VerificationStatuses = React.useMemo(
    () => !loading && Array.from(new Set(data.map((c) => c.requestStatusLabel))),
    [data, loading]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  return (
    <SmallFormGenerator
      initialValues={{ verifications: data }}
      editMode={editMode}
      minLabelWidth={minLabelWidth}
      onSubmit={(d) => saveItem(d.verifications)}
      loading={loading}
      saving={saving}
    >
      {!loading &&
        !currentRequestStatusIdLoading &&
        VerificationStatuses?.map((verificationLabel, i, k) => {
          return (
            <FieldGroup
              key={i}
              fieldsetProps={{
                title: (
                  <div
                    style={
                      currentRequestStatus ===
                      data.find((c) => c.requestStatusLabel === verificationLabel).requestStatus
                        ? { fontWeight: "bold" }
                        : null
                    }
                  >
                    {verificationLabel}
                  </div>
                ),
              }}
            >
              {data?.map((d, index, key) => {
                return d.requestStatusLabel === verificationLabel ? (
                  <FieldGroup key={index} columns={[3, 7]}>
                    <FGCheckboxInput
                      name={`verifications[${index}].isVerified`}
                      label={d.label}
                      requiredMark={d.required}
                      readonly={d.requestStatus !== currentRequestStatus}
                    />
                  </FieldGroup>
                ) : (
                  <></>
                );
              })}
            </FieldGroup>
          );
        })}
    </SmallFormGenerator>
  );
};
